/**
 * The Banks list (backend `routers/banks.py`, migration 0112; Pedro 2026-10-20).
 * Everyone signed in reads it (the account form's Mnemonic picker, the bank details an
 * account shows); only Admin adds, edits, deletes, and sees / dismisses review notes.
 */
import { api } from './client';
import type { UUID } from './types';

export interface BankContact {
  name: string | null; email: string | null; phone: string | null; mobile: string | null; notes: string | null;
  /** TRADE-FLOW (T-F4, 0122): this contact receives the trade orders sent for execution — "to" (one
   *  of the trade desk's addresses, chosen when composing) or "cc" (always copied); null = neither. */
  trade_role?: 'to' | 'cc' | null;
}
export interface BankContactOut extends BankContact { id: UUID; position: number }
export interface Bank {
  id: UUID; mnemonic: string; name: string; address: string | null; swift_bic: string | null;
  /** Alive accounts linked to it. */
  accounts: number; contacts_count: number;
  /** Review notes left by migration 0112 (Admin; 0 for others). */
  notes_count: number;
  updated_at: string;
}
export interface BankReviewNote {
  id: UUID; account_id: UUID | null; account_label: string | null; kind: string; detail: string;
}
export interface BankDetail extends Bank { contacts: BankContactOut[]; review_notes: BankReviewNote[] }
export interface BankInput {
  mnemonic: string; name: string; address: string | null; swift_bic: string | null; contacts: BankContact[];
}

export const MAX_BANK_CONTACTS = 10;

/** What each kind of review note means, in words. */
export const REVIEW_NOTE_KINDS: Record<string, string> = {
  mnemonic_changed: 'Mnemonic changed',
  generated_mnemonic: 'Mnemonic generated',
  mnemonic_clash: 'Mnemonic clash',
  mnemonic_invalid: 'Mnemonic not valid',
  address_differs: 'Address differed',
  swift_differs: 'SWIFT/BIC differed',
  contacts_over_limit: 'Contact not kept',
};

export const banksApi = {
  list: () => api.get<Bank[]>('/banks'),
  get: (id: UUID) => api.get<BankDetail>(`/banks/${id}`),
  create: (body: BankInput) => api.post<BankDetail>('/banks', body),
  update: (id: UUID, body: BankInput) => api.put<BankDetail>(`/banks/${id}`, body),
  remove: (id: UUID) => api.delete<Bank>(`/banks/${id}`),
  dismissNote: (bankId: UUID, noteId: UUID) => api.delete<void>(`/banks/${bankId}/review-notes/${noteId}`),
};
